import { DateTime } from "luxon";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain, DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import type { TaskSubjectWrite } from "@storyboard/web-client";
import type { DriverRow } from "@/types/driver";
import type { DispatchTableRow } from "./dispatchTableRow";
import {
  type ItemTarget,
  noteRequiredFor,
  openTaskOfTarget,
  type TaskAbout,
  type TaskFold,
  taskSubjectOfRow,
} from "./tasks";

/** One board the task picker's dropdown offers: a view, the viewer's own labeled as theirs. */
export type TaskViewOption = { id: string; label: string };

/**
 * The boards the task picker's dropdown lists: every view the workspace offers, the built-in default included, the
 * viewer's own first and labeled "(your board)".
 *
 * The rest keep the order they are given in.
 */
export function taskViewOptionsFor({
  views,
  currentViewId,
}: {
  views: readonly DispatchViewDefinition[];
  currentViewId: string;
}): TaskViewOption[] {
  const own = views.filter((view) => view.id === currentViewId);
  const rest = views.filter((view) => view.id !== currentViewId);
  return [
    ...own.map((view) => ({ id: view.id, label: `${view.label} (your board)` })),
    ...rest.map((view) => ({ id: view.id, label: view.label })),
  ];
}

/** One answer to what a new task is about, as the picker's About switch offers it. */
export type TaskAboutChoice = {
  about: TaskAbout;
  /** What the switch reads: "This message", "This event", "The driver only", "Each item", or "Each driver only". */
  label: string;
  /** Whether the task must carry a note: one about a driver with nothing waiting says nothing on its own. */
  requiresNote: boolean;
  /**
   * The open task the one message or event already carries, which Submit moves rather than doubles; always null for a
   * driver, who can carry many tasks, and for several rows.
   */
  current: OpenTask | null;
};

/** The driver a History entry's task can be about instead of the entry: the driver the panel is open on. */
export type TaskDriver = {
  workspaceMembershipId: string;
  /** The driver's name, as the picker's About header reads it. */
  label: string;
  /** How many things wait on the driver's channel; none means a task about the driver needs a note. */
  waiting: number;
};

/** The name a driver goes by in the picker: their name, else their alias. */
export function taskDriverLabel(driver: DriverRow): string {
  return driver.name ?? driver.alias ?? "Driver";
}

/** How many drivers the About header names before it counts them instead. */
const ABOUT_NAMES_SHOWN = 3;

/**
 * The drivers a task made from table rows is about, as the picker's About header names them: each row's driver once,
 * in row order.
 *
 * @param rows - The rows the task is made from.
 */
export function rowTaskDriverNames(rows: readonly DispatchTableRow[]): string[] {
  const byMembership = new Map(rows.map(({ driver }) => [driver.workspaceMembershipId, taskDriverLabel(driver)]));
  return [...byMembership.values()];
}

/** The picker's About header: the drivers' names, comma-separated, or {@link driverCountLabel} past a few of them. */
export function taskAboutHeading(names: readonly string[]): string {
  return names.length > ABOUT_NAMES_SHOWN ? driverCountLabel(names.length) : names.join(", ");
}

/** "N drivers": what the About header reads for several drivers whose names would not fit on its line. */
export function driverCountLabel(count: number): string {
  return count === 1 ? "1 driver" : `${count} drivers`;
}

/** What the switch reads for one message or event: a `feed_item` is a message, every other subject an event. */
function itemLabelOf(target: TaskSubjectWrite): string {
  return target.subject === "feed_item" ? "This message" : "This event";
}

/**
 * What a task made from table rows can be about, the default first: the message or event, then the driver.
 *
 * While the workspace keeps tasks about a message or event off, at the driver grain, and for rows with no message or
 * event a task can name, the only choice is the driver, and the picker shows no switch. Several rows read "Each item"
 * and "Each driver only".
 *
 * @param rows - The rows the task is made from.
 * @param grain - The view's grain.
 * @param fold - The workspace's open tasks, to find the one already on a lone message or event.
 * @param unacknowledgedByFeed - How many things wait on each channel, to decide whether a driver's task needs a note.
 * @param itemTasks - Whether the workspace lets a new task be about a message or event.
 */
export function rowTaskAboutChoices(
  rows: readonly DispatchTableRow[],
  grain: DispatchGrain,
  fold: TaskFold,
  unacknowledgedByFeed: ReadonlyMap<string, number>,
  itemTasks: boolean,
): TaskAboutChoice[] {
  const [lone] = rows.length === 1 ? rows : [];
  const driver: TaskAboutChoice = {
    about: "driver",
    label: lone ? "The driver only" : "Each driver only",
    requiresNote: noteRequiredFor(rows, "driver", unacknowledgedByFeed),
    current: null,
  };
  if (!itemTasks || grain === "driver") return [driver];
  // At the message grain, a row offers its message or event when a task can name one other than the driver.
  const items = rows.flatMap((row) => {
    const subject = taskSubjectOfRow(row, grain);
    return subject === null || subject.subject === "membership" ? [] : [subject];
  });
  const [loneItem] = items;
  if (loneItem === undefined) return [driver];
  const item: TaskAboutChoice = {
    about: "item",
    label: lone ? itemLabelOf(loneItem) : "Each item",
    requiresNote: noteRequiredFor(rows, grain, unacknowledgedByFeed),
    current: lone ? openTaskOfTarget(loneItem, fold) : null,
  };
  return [item, driver];
}

/**
 * What a task made from a History entry can be about, the default first: the message or event, then the driver the
 * panel is open on. An entry no task can name offers only the driver, as does every entry while the workspace keeps
 * tasks about a message or event off; a panel on no driver offers only the entry.
 *
 * @param target - The message or event, or null for an event of a subject no task names.
 * @param driver - The driver the History panel is open on, or null when it is open on none.
 * @param fold - The workspace's open tasks, to find the one already on the message or event.
 * @param itemTasks - Whether the workspace lets a new task be about a message or event.
 */
export function entryTaskAboutChoices(
  target: ItemTarget | null,
  driver: TaskDriver | null,
  fold: TaskFold,
  itemTasks: boolean,
): TaskAboutChoice[] {
  const choices: TaskAboutChoice[] = [];
  if (target && itemTasks) {
    choices.push({
      about: "item",
      label: itemLabelOf(target),
      requiresNote: false,
      current: openTaskOfTarget(target, fold),
    });
  }
  if (driver) {
    choices.push({ about: "driver", label: "The driver only", requiresNote: driver.waiting === 0, current: null });
  }
  return choices;
}

/**
 * What a task made from a History entry names: the entry itself, or the driver the panel is open on; null when the
 * chosen one is missing.
 *
 * @param target - The message or event, or null for an event of a subject no task names.
 * @param driverMembershipId - The driver the History panel is open on, or null.
 * @param about - What the dispatcher chose the task to be about.
 */
export function entryTaskSubject(
  target: ItemTarget | null,
  driverMembershipId: string | null,
  about: TaskAbout,
): TaskSubjectWrite | null {
  if (about === "item") return target;
  return driverMembershipId === null ? null : { subject: "membership", targetId: driverMembershipId };
}

/**
 * The muted line under the board when the chosen message or event already carries an open task: Submit moves that
 * task rather than making a second.
 *
 * @param current - The open task already on the subject.
 * @param viewLabelFor - A board's label by view id; null for a view the workspace no longer has.
 */
export function existingTaskLine(current: OpenTask, viewLabelFor: (viewId: string) => string | null): string {
  const board = current.pin ? viewLabelFor(current.pin.viewId) : null;
  return board ? `Already a task on ${board}. Submit moves it.` : "Already a task. Submit moves it.";
}

/** The expiries the picker offers a new task: none, an hour or four from now, the end of today, or a time typed in. */
export type TaskExpiryChoice = "never" | "hour" | "fourHours" | "endOfDay" | "custom";

/** The picker's expiry options, in the order it lists them, "Never" first as the default. */
export const TASK_EXPIRY_OPTIONS: readonly { value: TaskExpiryChoice; label: string }[] = [
  { value: "never", label: "Never" },
  { value: "hour", label: "In 1 hour" },
  { value: "fourHours", label: "In 4 hours" },
  { value: "endOfDay", label: "End of today" },
  { value: "custom", label: "Custom…" },
];

/**
 * The expiry as the picker holds it: the option chosen, and the custom field's `yyyy-MM-ddTHH:mm` in the viewer's zone.
 */
export type TaskExpiryInput = { choice: TaskExpiryChoice; custom: string };

/**
 * The instant a chosen expiry names, as UTC ISO; null for "Never"; undefined for a custom time that is empty, does not
 * parse, or is not after `now`, which Submit refuses.
 *
 * @param expiry - The option chosen and the custom field's value.
 * @param now - The viewer's clock, as epoch milliseconds; "End of today" is the end of its day in the viewer's zone.
 */
export function taskExpiryFor({ choice, custom }: TaskExpiryInput, now: number): string | null | undefined {
  const clock = DateTime.fromMillis(now);
  switch (choice) {
    case "never":
      return null;
    case "hour":
      return clock.plus({ hours: 1 }).toUTC().toISO() ?? undefined;
    case "fourHours":
      return clock.plus({ hours: 4 }).toUTC().toISO() ?? undefined;
    case "endOfDay":
      return clock.endOf("day").toUTC().toISO() ?? undefined;
    case "custom": {
      const at = DateTime.fromISO(custom);
      return at.isValid && at.toMillis() > now ? (at.toUTC().toISO() ?? undefined) : undefined;
    }
  }
}

/** What submitting the picker does: make a task about the chosen subject on a board, or move an open one there. */
export type TaskSubmission =
  | { kind: "make"; about: TaskAbout; viewId: string; note: string | null; expiresAt: string | null }
  | { kind: "move"; viewId: string };

/**
 * What Submit and Enter do now, or null while they do nothing: no board is chosen, a required note is empty, a custom
 * expiry is not a time after now, or a move's board is the one the task is already on.
 */
export function taskSubmissionFor(
  input:
    | {
        mode: "make";
        about: TaskAbout;
        viewId: string | null;
        note: string;
        requiresNote: boolean;
        /** The expiry chosen and the clock it is read against; omitted, the task never expires. */
        expiry?: TaskExpiryInput & { now: number };
      }
    | { mode: "move"; viewId: string | null; pinnedViewId: string | null },
): TaskSubmission | null {
  if (input.viewId === null) return null;
  if (input.mode === "move") {
    return input.viewId === input.pinnedViewId ? null : { kind: "move", viewId: input.viewId };
  }
  const trimmed = input.note.trim();
  if (input.requiresNote && trimmed === "") return null;
  const expiresAt = input.expiry ? taskExpiryFor(input.expiry, input.expiry.now) : null;
  if (expiresAt === undefined) return null;
  return { kind: "make", about: input.about, viewId: input.viewId, note: trimmed === "" ? null : trimmed, expiresAt };
}
